/**
 * Payouts: the balance we owe (from `overview`), what is still missing
 * before we can pay, and every payout we sent or tried to send.
 */
import { Chip } from "@nt/ui/chip";
import { PageHeader } from "@nt/ui/page-header";
import { buttonVariants } from "@nt/ui/recipes";
import { EmptyState } from "@nt/ui/states";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  TableStack,
  TableStackField,
  TableStackFields,
  TableStackRow,
  TableStackTitle,
} from "@nt/ui/table";
import {
  Banknote,
  CalendarDays,
  Circle,
  CircleCheck,
  Wallet,
} from "lucide-react";
import { Link } from "react-router";

import { QueryView, type QueryLike } from "@/components/query-view";
import { Section } from "@/components/section";
import { StatTile } from "@/components/stat-tile";
import { money, OWED_TO_US_NOTE, shortDate } from "@/lib/format";
import { useGet } from "@/lib/queries";
import type { OverviewData } from "@/lib/types";
import { edgeToEdge } from "@/pages/customers/shared";
import type { TaxStatus } from "@/pages/settings/tax-form";

export interface PayoutRow {
  id: string;
  period: string;
  amountCents: number;
  currency: string;
  /** `paid` or `failed`: the API lists no other payout status. */
  status: string;
  reference: string | null;
  paidAt: string | null;
  failureReason: string | null;
}

export interface PayoutsData {
  payouts: PayoutRow[];
  overview: OverviewData;
  /** From the tax form read; undefined while it loads or when it fails. */
  taxFormState?: TaxStatus["state"];
  /** From the same read: an approved form still pays, even when the latest form was rejected. */
  taxFormInForce?: boolean;
}

const RECOVERY =
  "Check your payout method, then write to support@nonresident.tax so we can send it again.";

/** "September 2026" for a `YYYY-MM` period; anything else as it came. */
export function periodLabel(period: string): string {
  const m = /^(\d{4})-(\d{2})$/.exec(period);
  if (!m) return period;
  const d = new Date(Date.UTC(Number(m[1]), Number(m[2]) - 1, 1));
  return d.toLocaleDateString("en-GB", {
    month: "long",
    year: "numeric",
    timeZone: "UTC",
  });
}

const PayoutChip = ({ status }: { status: string }) =>
  status === "paid" ? (
    <Chip tone="success">Paid</Chip>
  ) : (
    <Chip tone="danger">Failed</Chip>
  );

function FailureNote({ p }: { p: PayoutRow }) {
  if (p.status === "paid") return null;
  return (
    <p className="mt-1 max-w-sm text-compact text-pretty text-danger">
      {p.failureReason
        ? `${p.failureReason.trim().replace(/\.+$/, "")}. `
        : "The transfer failed. "}
      {RECOVERY}
    </p>
  );
}

/**
 * `setup.taxFormNeeded` is true only once the balance reaches the minimum, so
 * its false is not "the form is in": that fact comes from the tax form read. A
 * state we do not have yet never counts as done.
 */
function Checklist({
  setup,
  taxFormState,
  taxFormInForce,
}: {
  setup: OverviewData["setup"];
  taxFormState?: TaxStatus["state"];
  taxFormInForce?: boolean;
}) {
  if (!setup) return null;
  const taxDone =
    !setup.taxFormNeeded &&
    (taxFormState === "approved" ||
      taxFormState === "pending" ||
      (taxFormState === "rejected" && taxFormInForce === true));
  const taxUnknown = taxFormState === undefined && !setup.taxFormNeeded;
  if (setup.payoutMethod && (taxDone || taxUnknown)) return null;
  const steps = [
    {
      key: "payout-method",
      done: setup.payoutMethod,
      title: "Add a payout method",
      body: "Tell us where to send your money: a Wise account or a bank account.",
      to: "/settings/payout-method",
      action: "Add payout method",
    },
    {
      key: "tax-form",
      done: taxDone,
      title: "Send your tax form",
      body: setup.taxFormNeeded
        ? "We need a W-9 or W-8 on file before we can pay you."
        : "Needed once your balance reaches the minimum.",
      to: "/settings/tax-form",
      action: "Send tax form",
    },
  ];
  return (
    <Section
      title="Before we can pay you"
      description="Your balance keeps growing, but we hold payouts until both steps are done."
    >
      <ul className="flex flex-col divide-y divide-border">
        {steps.map((s) => (
          <li
            key={s.key}
            className="flex flex-col gap-3 py-3 first:pt-0 last:pb-0 sm:flex-row sm:items-center sm:justify-between"
          >
            <div className="flex min-w-0 items-start gap-3">
              {s.done ? (
                <CircleCheck
                  aria-hidden
                  className="mt-0.5 size-5 shrink-0 text-success"
                />
              ) : (
                <Circle
                  aria-hidden
                  className="mt-0.5 size-5 shrink-0 text-muted-foreground"
                />
              )}
              <div className="flex min-w-0 flex-col gap-0.5">
                <p className="text-sm font-medium text-foreground">{s.title}</p>
                <p className="text-sm text-muted-foreground">{s.body}</p>
              </div>
            </div>
            {s.done ? (
              <Chip tone="success" className="self-start sm:self-center">
                Done
              </Chip>
            ) : (
              <Link
                to={s.to}
                className={buttonVariants({
                  size: "sm",
                  className: "self-start sm:self-center",
                })}
              >
                {s.action}
              </Link>
            )}
          </li>
        ))}
      </ul>
    </Section>
  );
}

function History({ data }: { data: PayoutsData }) {
  const { payouts, overview } = data;
  if (payouts.length === 0) {
    return (
      <EmptyState
        icon={<Banknote />}
        title="No payouts yet"
        description={`We pay on the 1st of each month once your payable balance reaches ${money(overview.minPayoutCents)}. A fee becomes payable after its ${overview.holdDays}-day hold. Your payout method and tax form need to be in place first.`}
      />
    );
  }
  return (
    <>
      <div className={`hidden sm:block ${edgeToEdge}`}>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Period</TableHead>
              <TableHead className="text-right">Amount</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Reference</TableHead>
              <TableHead>Paid on</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {payouts.map((p) => (
              <TableRow key={p.id}>
                <TableCell className="whitespace-nowrap">
                  {periodLabel(p.period)}
                </TableCell>
                <TableCell className="text-right">
                  {money(p.amountCents, p.currency)}
                </TableCell>
                <TableCell>
                  <PayoutChip status={p.status} />
                  <FailureNote p={p} />
                </TableCell>
                <TableCell className="font-mono text-compact">
                  {p.reference ?? ""}
                </TableCell>
                <TableCell className="whitespace-nowrap">
                  {shortDate(p.paidAt)}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
      <TableStack className="sm:hidden">
        {payouts.map((p) => (
          <TableStackRow key={p.id} className="px-(--card-spacing)">
            <TableStackTitle>
              <span>{periodLabel(p.period)}</span>
              <PayoutChip status={p.status} />
            </TableStackTitle>
            <TableStackFields>
              <TableStackField label="Amount">
                {money(p.amountCents, p.currency)}
              </TableStackField>
              {p.reference ? (
                <TableStackField label="Reference">
                  {p.reference}
                </TableStackField>
              ) : null}
              <TableStackField label="Paid on">
                {shortDate(p.paidAt) || "Not paid"}
              </TableStackField>
            </TableStackFields>
            <FailureNote p={p} />
          </TableStackRow>
        ))}
      </TableStack>
    </>
  );
}

export function PayoutsView({ data }: { data: PayoutsData }) {
  const { balance, minPayoutCents, nextPayoutDate, setup } = data.overview;
  return (
    <>
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
        {balance.payableCents < 0 ? (
          <StatTile
            label="Owed to us"
            value={money(Math.abs(balance.payableCents))}
            hint={OWED_TO_US_NOTE}
            icon={Wallet}
          />
        ) : (
          <StatTile
            label="Payable now"
            value={money(balance.payableCents)}
            hint={`We pay balances of ${money(minPayoutCents)} or more.`}
            icon={Wallet}
          />
        )}
        <StatTile
          label="Next payout date"
          value={shortDate(nextPayoutDate)}
          hint="We pay on the 1st of each month."
          icon={CalendarDays}
        />
        <StatTile
          label="Paid to date"
          value={money(balance.paidCents)}
          icon={Banknote}
        />
      </div>
      <Checklist
        setup={setup}
        taxFormState={data.taxFormState}
        taxFormInForce={data.taxFormInForce}
      />
      <Section
        title="Payout history"
        description="Every payout we sent you, newest first."
        contentClassName={data.payouts.length ? "px-0" : undefined}
      >
        <History data={data} />
      </Section>
    </>
  );
}

export function PayoutsScreen({ query }: { query: QueryLike<PayoutsData> }) {
  return (
    <QueryView query={query} what="your payouts" rows={5}>
      {(data) => <PayoutsView data={data} />}
    </QueryView>
  );
}

export function Payouts() {
  const payouts = useGet<{ payouts: PayoutRow[] }>(
    "payouts",
    "/api/partner/payouts",
  );
  const overview = useGet<OverviewData>("overview", "/api/partner/overview");
  const taxForm = useGet<TaxStatus>("tax-form", "/api/partner/tax-form");
  const isError = payouts.isError || overview.isError;
  const query: QueryLike<PayoutsData> = {
    isPending: !isError && (payouts.isPending || overview.isPending),
    isError,
    error: payouts.error ?? overview.error,
    data:
      payouts.data && overview.data
        ? {
            payouts: payouts.data.payouts,
            overview: overview.data,
            taxFormState: taxForm.data?.state,
            taxFormInForce: taxForm.data?.approvedFormInForce,
          }
        : undefined,
    refetch: () => {
      if (payouts.isError) void payouts.refetch();
      if (overview.isError) void overview.refetch();
    },
  };
  return (
    <>
      <PageHeader
        title="Payouts"
        description="What you can be paid, when, and every payout we sent."
      />
      <PayoutsScreen query={query} />
    </>
  );
}
